import ReactMarkdown from "react-markdown";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const AI_FAQS = [
  {
    id: "ai-why-not-generic",
    q: "Why choose CV Folks AI instead of a generic AI tool?",
    a: "Generic AI tools generate text — you need to know what to ask, and you get general-purpose writing with no CV methodology behind it. CV Folks AI walks you through a **professional CV writing process** built by experienced UK CV writers: recruiter-informed structure, achievement-focused bullets, ATS-safe formatting and professional templates. You follow the process; the expertise is built in.",
  },
  {
    id: "ai-uk-market",
    q: "Is it designed for the UK job market?",
    a: "Yes — exclusively. **UK English** spelling and tone, UK CV conventions (no photos, no 'resume'-style formats), section ordering UK recruiters expect, and writing guidance shaped by what UK employers shortlist on. It's built by UK CV writers for UK job seekers.",
  },
  {
    id: "ai-what-is",
    q: "What does the £9.99 Online AI CV Builder actually give me?",
    a: "A guided wizard, a live preview, an **ATS-friendly UK CV** and a **matching cover letter** — all downloadable as PDF and DOCX. You also get unlimited edits from your dashboard for 6 months.",
  },
  {
    id: "ai-how-fast",
    q: "How long does it take to build a CV online with the AI?",
    a: "**Under 5 minutes** for the full wizard, including the AI generation. Most users go from blank page to interview-ready CV in a single sitting.",
  },
  {
    id: "ai-quality",
    q: "Is an AI-written CV actually good enough to get interviews?",
    a: "Yes. Our AI CV maker isn't a generic chatbot — it's guided by professional CV writing methodology developed by UK CV writers, recruiter-informed best practices and ATS optimisation principles. Many users get interview invites within their first two weeks of applying.",
  },
  {
    id: "ai-import",
    q: "Can I import my existing CV?",
    a: "Yes. Upload a **PDF, DOCX or TXT (up to 5 MB)** at Step 1 and our AI CV creator will pre-fill the wizard automatically. Just review, tweak and generate.",
  },
  {
    id: "ai-ats",
    q: "Is the AI-generated CV ATS-friendly?",
    a: "Every template (Classic Bloom, Modern Charcoal, Compact One-Pager) is built to pass **Applicant Tracking Systems** used by UK recruiters — no columns, no images embedded in text, proper section headers, keyword-optimised structure.",
  },
  {
    id: "ai-cover-letter",
    q: "Do I get a cover letter as well?",
    a: "Yes — a **professional 3–4 paragraph cover letter** is generated alongside your CV, tailored to the same target role. It's included in the £9.99 price. No extra charge.",
  },
  {
    id: "ai-edit",
    q: "Can I edit the CV after the AI has written it?",
    a: "Yes — once you unlock for £9.99, you can edit every section (summary, skills, roles, bullets, dates, education), switch templates, re-download the PDF/DOCX and come back to tweak it for **6 months** at no extra cost.",
  },
  {
    id: "ai-lock",
    q: "Why is the CV locked before payment?",
    a: "You can preview the top portion of your AI-written CV for free. Full editing, template switching and downloads unlock once the one-off **£9.99** payment is captured. It's how we keep the price this low without a subscription.",
  },
  {
    id: "ai-credit",
    q: "What if I want a human writer later?",
    a: "Your **£9.99 comes off** any Professional CV Writing package — Standard CV (£50), Standard Package (£95), Premium CV (£125) or Premium Package (£155). Nothing lost, and you keep the AI CV as a working draft to share with your writer.",
  },
  {
    id: "ai-privacy",
    q: "Is my data private?",
    a: "Yes. Your CV content stays on your account — we never share, sell or publish it. You can delete any CV from your dashboard at any time.",
  },
  {
    id: "ai-payment",
    q: "How does payment work?",
    a: "Secure one-off payment of **£9.99 via PayPal** (card or PayPal balance). No subscription, no recurring charges, no hidden fees. UK company, VAT-registered.",
  },
  {
    id: "ai-refund",
    q: "Can I get a refund?",
    a: "You preview the AI-generated CV before you pay, so you know exactly what you're unlocking. If there's a genuine technical issue with your CV, contact us and we'll make it right.",
  },
];

const MDLink = (props) => (
  <a
    {...props}
    className="text-brand underline hover:no-underline"
    target="_blank"
    rel="noopener noreferrer"
  />
);
const MDStrong = (props) => (
  <strong {...props} className="font-semibold text-foreground" />
);
const MD_COMPONENTS = { a: MDLink, strong: MDStrong };

export default function AiCvFAQ() {
  return (
    <section
      id="faq"
      data-testid="ai-cv-faq-section"
      className="py-24 md:py-32 bg-muted/30"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="label-eyebrow">FAQ · AI CV Builder</p>
          <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
            Everything about building a CV online with our AI.
          </h2>
          <p className="mt-4 text-sm md:text-base text-foreground/70">
            Answers to the questions we hear most about the £9.99 Online AI CV
            Builder, our AI CV maker, cover letters, ATS optimisation and
            upgrading to a professional UK CV writer.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-3">
          {AI_FAQS.map((item) => (
            <AccordionItem
              key={item.id}
              value={item.id}
              data-testid={`ai-faq-item-${item.id}`}
              className="rounded-2xl border border-border bg-card px-5 md:px-6"
            >
              <AccordionTrigger
                data-testid={`ai-faq-trigger-${item.id}`}
                className="text-left font-serif text-lg md:text-xl font-semibold hover:no-underline py-5"
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base text-foreground/80 leading-relaxed pb-6">
                <div className="faq-answer space-y-3">
                  <ReactMarkdown components={MD_COMPONENTS}>
                    {item.a || ""}
                  </ReactMarkdown>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
